import { Journey } from '../../pages/customer-profile/types/GrowerService';
import { API } from '../common/api';
import { BASE_URL, FIELD_URL } from '../common/env';
import Field from '../../pages/map/types/Field';
import i18next from 'i18next';
import { User } from '../../redux/reducers/user-reducer';
import { showToast } from '../../pages/map/service/common/utils';
import { Feature } from '@turf/helpers';

export const upsertShapefile = (
  journey: Journey,
  shapefileStatus: string,
  country: string,
  contractPaymentOption?: string,
  estimatedCarbon?: number,
) => {
  return API.put(`${FIELD_URL}/field-service/shapefiles/status`, {
    journeyId: journey?.id,
    shapefileStatus,
    country,
    estimatedCarbon,
    contractPaymentOption,
  }).catch((err) => {
    console.log(`Couldn't upsert shapefile: upsertShapefile error - ${JSON.stringify(err)}`);
    throw err;
  });
};

export const getPracticeData = (fields: Field[]) => {
  const practiceData: any = {};
  fields.forEach((f) => {
    if (f.properties?.practices?.length) {
      f.properties.practices.forEach((practice: string) => {
        let index: string;
        const option = practice === 'nitrogenReduction' || practice === 'nitrogenOptimization' ? 'ANNUAL' : f.properties?.paymentOption;
        //Contract paymentOptions is used to dictate brazilian field paymentOptions
        if (f.properties?.country === 'Brazil') {
          index = practice;
        } else {
          index = practice + option;
        }
        if (!practiceData[index]) {
          practiceData[index] = {
            name: practice,
            option: option,
            platform: Number(f.properties?.[i18next.language === 'pt-BR' ? 'areaHectare' : 'area']),
            sf: 0,
          };
        } else {
          practiceData[index].platform += Number(f.properties?.[i18next.language === 'pt-BR' ? 'areaHectare' : 'area']);
        }
      });
    }
  });

  for (const key in practiceData) {
    practiceData[key].sf = Number(practiceData[key].sf.toFixed(2));
    practiceData[key].platform = Number(practiceData[key].platform.toFixed(2));
  }

  const res = Object.keys(practiceData).map((k) => practiceData[k]);
  res.sort((a, b) => (a.name > b.name ? 1 : -1));
  return res;
};

export const notifyUpdate = (t, producerName: string, urlProducerProfile: string, updatedBy: User, brBoundaryApproval: boolean) => {
  let channel: string;
  let text: string;
  if (brBoundaryApproval) {
    channel = 'NELI_DM';
    text = `*Vendas*: O arquivo de limites do produtor *_${producerName}_* (que já tinha passado por análise de hectares) *foi atualizado (os limites mudaram).* Clique aqui para ver o novo arquivo e verifique se uma nova análise é necessária: ${urlProducerProfile}.\nLembre-se de aprovar os limites novamente.`;
  } else {
    let username: string | undefined;
    if (updatedBy?.firstName) {
      username = `${updatedBy.firstName} ${updatedBy.lastName}`;
    } else {
      username = updatedBy.fullName ?? updatedBy.email;
    }
    channel = 'BR_SALES_GC';
    text = `*Vendas*: O arquivo de limites do produtor _${producerName}_ foi atualizado na Plataforma${
      username ? ` por *${username}*` : ''
    }. Clique aqui para ver o novo arquivo: ${urlProducerProfile}.\nLembre-se de modificar a estimativa para ter as mesmas informações dos limites (práticas e hectares).`;
  }
  const body = {
    username: 'Agoro Bot',
    channel,
    text,
  };
  return API.post(`${BASE_URL}/base/slack-message`, body).catch(() => {
    showToast(t('fieldFlow.errors.error'), t('fieldFlow.errors.notification'), 'error', 'sales-boundary-notification-error');
  });
};

export const getSyncAvailabilityEstimate = (fields: Feature[], estimate: { data: any }, country: string) => {
  const shortCodeToPracticeLookup: any = {
    NT: 'noTillage',
    IT: 'reducedTillage',
    NRR: 'nitrogenReduction',
    NO: 'nitrogenOptimization',
    LA: 'legume',
    RPF: 'fertilization',
    RPIG: 'grazing',
    RPBS: 'seeding',
    ICL1: 'ICL',
    ICL2: 'ICL',
    CC: 'coverPerennial',
    CP: 'coverPerennial',
    CA: 'coverAnnual',
    IN: 'inoculant',
  };

  const currentPractices = [
    {
      name: 'coverAnnual',
      shortCode: 'CA',
    },
    {
      name: 'coverPerennial',
      shortCode: 'CP',
    },
    {
      name: 'ICL',
      shortCode: 'ICL1',
    },
  ];

  const clonedFields = fields.map((field) => {
    return { ...field };
  });

  const isBR = country === 'br';
  const areaIdentifier = isBR ? 'areaHectare' : 'area';

  for (const scenario of estimate.data) {
    const fieldIndex = clonedFields.findIndex((field) => field.id === scenario.id || field.properties?.[areaIdentifier] === scenario.area);
    if (fieldIndex === -1) {
      return false;
    }

    const field = clonedFields[fieldIndex];
    clonedFields.splice(fieldIndex, 1);

    // Estimate area is area.toFixed(2), so the comparsion does the same rounding
    if (isBR && Number(scenario.area).toFixed(2) !== Number(field?.properties?.[areaIdentifier]).toFixed(2)) {
      return false;
    }

    const filteredScenarioPractices = scenario.practices.filter((prac) => currentPractices.some((p) => p.shortCode === prac.shortCode));
    const filteredFieldPractices = field.properties?.practices.filter((prac) => currentPractices.some((p) => p.name === prac));

    if (filteredScenarioPractices.length !== (filteredFieldPractices.length ?? 0)) {
      return false;
    }

    for (const practice of filteredScenarioPractices) {
      if (filteredFieldPractices.indexOf(shortCodeToPracticeLookup[practice.shortCode]) === -1) {
        return false;
      }
    }
  }
  return true;
};

export const getLastPossibleVersionEndDate = (journeyId: string) => {
  return API.get(`${FIELD_URL}/field-service/paddocks/layouts/last-date`, { journeyId }).then((res) => res.data.lastDate);
};
